import { forwardRef, useCallback, useEffect, useState } from 'react';
import { demands, type Demand } from '../content';
import { formatNewsDate } from './News';

/**
 * Render the quote with its key sentence emphasized. Split on the substring
 * rather than using dangerouslySetInnerHTML — this is a direct quotation of a
 * company statement and markup must not be able to creep into it.
 */
function Quote({ quote }: { quote: NonNullable<Demand['quote']> }) {
  const at = quote.text.indexOf(quote.emphasize);
  const before = at === -1 ? quote.text : quote.text.slice(0, at);
  const after = at === -1 ? '' : quote.text.slice(at + quote.emphasize.length);

  return (
    <figure className="pullquote">
      <blockquote className="pullquote__body">
        {before}
        {at !== -1 && <mark className="pullquote__mark">{quote.emphasize}</mark>}
        {after}
      </blockquote>
      <figcaption className="pullquote__by">{quote.attribution}</figcaption>
    </figure>
  );
}

interface CardProps {
  demand: Demand;
  index: number;
  open: boolean;
  onToggle: () => void;
}

function DemandCard({ demand, index, open, onToggle }: CardProps) {
  const panelId = `${demand.id}-detail`;
  const headingId = `${demand.id}-heading`;

  return (
    <li
      className={`card-demand${open ? ' card-demand--open' : ''}${demand.wonOn ? ' card-demand--won' : ''}`}
      id={demand.id}
    >
      <span className="card-demand__number" aria-hidden="true">
        {String(index + 1).padStart(2, '0')}
      </span>

      {demand.wonOn && (
        <p className="card-demand__won">Won · {formatNewsDate(demand.wonOn)}</p>
      )}

      <h3 className="card-demand__title" id={headingId}>
        {demand.headline}
      </h3>

      {demand.ask && <p className="card-demand__ask">{demand.ask}</p>}

      <button
        type="button"
        className="card-demand__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {open ? 'Less' : 'Read the detail'}
        <span className="card-demand__chevron" aria-hidden="true" />
      </button>

      {/* Kept in the DOM only when open so collapsed cards stay cheap and their
          links stay out of the tab order without needing inert/tabindex juggling. */}
      {open && (
        <div className="card-demand__panel" id={panelId} role="region" aria-labelledby={headingId}>
          {demand.body.map((para, j) => (
            <div key={para.slice(0, 40)}>
              <p className="card-demand__body">{para}</p>
              {demand.quote && demand.quoteAfter === j && <Quote quote={demand.quote} />}
            </div>
          ))}

          <ul className="sources__list">
            {demand.sources.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noreferrer noopener">
                  {source.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

export const Demands = forwardRef<HTMLElement>(function Demands(_props, ref) {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  // A link to #hr9720 should land on an OPEN card — otherwise following one of
  // the hero links scrolls you to a collapsed box and looks broken.
  useEffect(() => {
    const openFromHash = () => {
      const id = window.location.hash.slice(1);
      if (demands.some((d) => d.id === id)) {
        setOpenIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
      }
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, []);

  return (
    <section className="section" id="demands" ref={ref} aria-labelledby="demands-heading">
      <div className="section__inner">
        <h2 id="demands-heading" className="section__title">
          Our Demands
        </h2>

        <ol className="card-demands">
          {demands.map((demand, i) => (
            <DemandCard
              key={demand.id}
              demand={demand}
              index={i}
              open={openIds.has(demand.id)}
              onToggle={() => toggle(demand.id)}
            />
          ))}
        </ol>
      </div>
    </section>
  );
});
